/**
 * Language switch. English and French at launch; Khmer later (ADR-0003).
 *
 * Deliberately *not* hidden on small screens. The previous version was
 * `hidden sm:inline-flex`, which meant a phone user could not change language at all
 * -- and this is the only language control in the application. Mobile is review-first
 * by design (14.3), and reviewing a calculation in the wrong language is not reviewing.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { LOCALE_LABELS, SUPPORTED_LOCALES, setLocale, type Locale } from '@/i18n'

export function LanguageToggle({ className }: { className?: string }) {
  const { i18n, t } = useTranslation()
  const current = (i18n.language.slice(0, 2) as Locale) ?? 'en'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t('language.label')}
        className={cn(
          'flex h-11 items-center gap-1.5 rounded-md px-2 text-text-muted',
          'hover:bg-surface-raised hover:text-text',
          className,
        )}
      >
        <Languages size={18} aria-hidden />
        {/* The code, not the endonym: it must fit next to the theme button on a
            390px viewport. The full name is in the menu. */}
        <span className="text-xs font-medium uppercase">{current}</span>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[10rem] rounded-md border border-border bg-surface-raised p-1 shadow-[var(--shadow-md)]"
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <DropdownMenu.Item
              key={locale}
              onSelect={() => setLocale(locale)}
              // A language name is always written in its own language, never
              // translated -- a French speaker looks for "Français", not "French".
              lang={locale}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none',
                'data-[highlighted]:bg-surface data-[highlighted]:text-text',
              )}
            >
              <span className="flex-1">{LOCALE_LABELS[locale]}</span>
              {current === locale && <Check size={14} aria-hidden />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
